import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowRight, Loader2, Mail, Lock } from 'lucide-react';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="font-inter relative min-h-screen w-full overflow-hidden bg-black flex items-center justify-center px-4 py-12">
      {/* Background video — same locally-bundled asset as the homepage hero. */}
      <video
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        controls={false}
        poster="/hero-poster.jpg"
        className="absolute inset-0 w-full h-full object-cover z-0"
      >
        <source src="/hero.webm" type="video/webm" />
        <source src="/hero.mp4" type="video/mp4" />
      </video>

      {/* Slightly lighter overlay so the video shows through behind the form. */}
      <div className="absolute inset-0 z-10 bg-gradient-to-b from-black/65 via-black/45 to-black/70" />

      {/* Login card */}
      <div className="relative z-20 w-full max-w-md">
        {/* Brand mark — uses the same /logo.png as the rest of the app.
            White-on-dark works because the source PNG has a transparent
            background, so it sits cleanly on the video without a tile. */}
        <Link to="/" className="flex justify-center mb-8">
          <img
            src="/logo.png"
            alt="Orbit by Pear Media"
            className="h-12 w-auto object-contain drop-shadow-lg"
          />
        </Link>

        <div className="bg-white/[0.06] backdrop-blur-xl border border-white/15 rounded-2xl shadow-2xl p-8">
          <div className="mb-7">
            <h1 className="text-2xl font-bold text-white tracking-tight">Welcome back</h1>
            <p className="text-sm text-white/60 mt-1.5">Sign in to your Orbit account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="px-3 py-2.5 rounded-lg bg-red-500/15 border border-red-500/30 text-red-200 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <label htmlFor="email" className="text-xs font-medium text-white/70 uppercase tracking-wider">
                Email
              </label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                <input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-black/30 border border-white/15 text-white placeholder-white/30 focus:outline-none focus:border-orange-400 focus:bg-black/40 transition-colors"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="password" className="text-xs font-medium text-white/70 uppercase tracking-wider">
                Password
              </label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                <input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-black/30 border border-white/15 text-white placeholder-white/30 focus:outline-none focus:border-orange-400 focus:bg-black/40 transition-colors"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full mt-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 disabled:cursor-not-allowed text-white font-semibold shadow-lg shadow-orange-500/30 transition-all"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Signing in…
                </>
              ) : (
                <>
                  Sign in
                  <ArrowRight size={16} />
                </>
              )}
            </button>

            <p className="text-center text-sm text-white/60 pt-2">
              Don't have an account?{' '}
              <Link to="/register" className="text-orange-400 hover:text-orange-300 font-medium transition-colors">
                Sign up
              </Link>
            </p>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-white/40">
          © 2026 Pear Media LLC · Orbit
        </p>
      </div>
    </div>
  );
}
